import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode, type CSSProperties } from "react";
import { autoUpdate, flip, offset, size, useDismiss, useFloating, useInteractions, FloatingPortal } from "@floating-ui/react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { controlBase } from "./form";

// Custom select: never the native select element. Listbox semantics, keyboard navigation (arrows, Home / End, Enter,
// Escape, type-ahead), optional search, multi select with chips, rendered in a portal so it is never clipped.


/**
 * Floating panels stay invisible until Floating UI has measured them, and every useFloating uses `transform: false`
 * (top / left positioning): the pop-in animation animates transform and would otherwise override the position, so
 * the panel showed in the corner of the screen before jumping to its place.
 */
export function placedStyles(styles: CSSProperties, isPositioned: boolean): CSSProperties {
  return isPositioned ? styles : { ...styles, visibility: "hidden" };
}

export interface SelectOption<V extends string = string> {
  value: V;
  label: string;
  description?: string;
  icon?: ReactNode;
  disabled?: boolean;
  group?: string;
}

interface BaseProps<V extends string> {
  options: Array<SelectOption<V>>;
  placeholder?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
  id?: string;
  "aria-label"?: string;
  emptyText?: string;
  /** Custom trigger content for the selected option. */
  renderValue?: (option: SelectOption<V>) => ReactNode;
  variant?: "default" | "ghost";
  align?: "start" | "end";
  menuWidth?: number;
}

export interface SelectProps<V extends string> extends BaseProps<V> {
  value: V | null | undefined;
  onChange: (value: V) => void;
  clearable?: boolean;
  onClear?: () => void;
}

const normalize = (s: string) =>
  s
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase();

function useListbox<V extends string>(
  options: Array<SelectOption<V>>,
  opts: { searchable?: boolean; disabled?: boolean; align?: "start" | "end"; menuWidth?: number },
) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1);
  const listRef = useRef<HTMLDivElement | null>(null);
  const typeahead = useRef({ text: "", at: 0 });

  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open,
    onOpenChange: (next) => {
      setOpen(next);
      if (!next) setQuery("");
    },
    placement: opts.align === "end" ? "bottom-end" : "bottom-start",
    whileElementsMounted: autoUpdate,
    transform: false,
    middleware: [
      offset(6),
      flip({ padding: 8 }),
      size({
        padding: 8,
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            minWidth: `${Math.max(rects.reference.width, opts.menuWidth ?? 0)}px`,
            maxHeight: `${Math.max(160, Math.min(340, availableHeight))}px`,
          });
        },
      }),
    ],
  });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  const filtered = useMemo(() => {
    if (!opts.searchable || !query.trim()) return options;
    const q = normalize(query.trim());
    return options.filter((o) => normalize(`${o.label} ${o.description ?? ""}`).includes(q));
  }, [options, query, opts.searchable]);

  useEffect(() => {
    if (!open || active < 0) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const move = (delta: number) => {
    if (filtered.length === 0) return;
    let next = active;
    for (let i = 0; i < filtered.length; i += 1) {
      next = (next + delta + filtered.length) % filtered.length;
      if (!filtered[next]?.disabled) break;
    }
    setActive(next);
  };

  const onTypeahead = (key: string) => {
    const now = Date.now();
    typeahead.current.text = now - typeahead.current.at > 700 ? key : typeahead.current.text + key;
    typeahead.current.at = now;
    const q = normalize(typeahead.current.text);
    const idx = filtered.findIndex((o) => !o.disabled && normalize(o.label).startsWith(q));
    if (idx >= 0) setActive(idx);
  };

  return { open, setOpen, query, setQuery, active, setActive, filtered, refs, floatingStyles: placedStyles(floatingStyles, isPositioned), isPositioned, getReferenceProps, getFloatingProps, listRef, move, onTypeahead, disabled: opts.disabled };
}

function triggerClasses(size: "sm" | "md" | "lg", invalid?: boolean, variant: "default" | "ghost" = "default") {
  const h = size === "sm" ? "h-8.5 text-[13px]" : size === "lg" ? "h-11" : "h-10";
  if (variant === "ghost") {
    return cn("inline-flex items-center gap-2 rounded-[10px] border border-line-strong bg-panel-2 px-3 text-[13px] text-ink hover:bg-panel-3", h);
  }
  return cn(controlBase, h, "flex items-center gap-2 px-3 text-left", invalid && "border-danger/70");
}

function OptionRow<V extends string>({
  option,
  index,
  active,
  selected,
  multiple,
  onPick,
  onHover,
}: {
  option: SelectOption<V>;
  index: number;
  active: boolean;
  selected: boolean;
  multiple?: boolean;
  onPick: () => void;
  onHover: () => void;
}) {
  return (
    <div
      role="option"
      aria-selected={selected}
      aria-disabled={option.disabled || undefined}
      data-index={index}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => !option.disabled && onPick()}
      onMouseEnter={onHover}
      className={cn(
        "flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px]",
        active && "bg-panel-3",
        selected && !multiple && "text-white",
        option.disabled && "cursor-not-allowed opacity-45",
      )}
    >
      {multiple ? (
        <span className={cn("flex size-4 shrink-0 items-center justify-center rounded border", selected ? "border-primary bg-primary text-white" : "border-line-strong")}>
          {selected ? <Check className="size-3" strokeWidth={3} /> : null}
        </span>
      ) : null}
      {option.icon ? <span className="flex shrink-0 items-center [&>svg]:size-4">{option.icon}</span> : null}
      <span className="min-w-0 flex-1">
        <span className="block truncate">{option.label}</span>
        {option.description ? <span className="block truncate text-[11.5px] text-muted">{option.description}</span> : null}
      </span>
      {!multiple && selected ? <Check className="size-4 shrink-0 text-primary-400" /> : null}
    </div>
  );
}

function Menu<V extends string>({
  lb,
  searchable,
  searchPlaceholder,
  emptyText,
  listId,
  children,
}: {
  lb: ReturnType<typeof useListbox<V>>;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
  listId: string;
  children: ReactNode;
}) {
  if (!lb.open) return null;
  return (
    <FloatingPortal>
      <div
        ref={lb.refs.setFloating}
        style={lb.floatingStyles}
        {...lb.getFloatingProps()}
        className={cn("z-[80] flex flex-col overflow-hidden rounded-xl border border-line-strong bg-[#0d1730] shadow-pop", lb.isPositioned && "animate-pop-in")}
      >
        {searchable ? (
          <div className="border-b border-line p-2">
            <div className="relative flex items-center">
              <Search className="pointer-events-none absolute left-2.5 size-3.5 text-muted" />
              <input
                autoFocus
                value={lb.query}
                onChange={(e) => {
                  lb.setQuery(e.target.value);
                  lb.setActive(0);
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    lb.move(1);
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    lb.move(-1);
                  } else if (e.key === "Enter") {
                    e.preventDefault();
                    const el = lb.listRef.current?.querySelector<HTMLElement>(`[data-index="${lb.active}"]`);
                    el?.click();
                  } else if (e.key === "Escape") {
                    lb.setOpen(false);
                  }
                }}
                placeholder={searchPlaceholder ?? "Rechercher..."}
                aria-controls={listId}
                className="h-8.5 w-full rounded-lg border border-line bg-panel-2 pl-8 pr-2 text-[13px] text-ink placeholder:text-faint focus:border-primary/60 focus:outline-none"
              />
            </div>
          </div>
        ) : null}
        <div ref={lb.listRef} id={listId} role="listbox" className="min-h-0 flex-1 overflow-y-auto p-1.5">
          {lb.filtered.length === 0 ? <div className="px-3 py-6 text-center text-[13px] text-muted">{emptyText ?? "Aucun résultat"}</div> : children}
        </div>
      </div>
    </FloatingPortal>
  );
}

export function Select<V extends string>({
  value,
  onChange,
  options,
  placeholder = "Sélectionner",
  searchable,
  searchPlaceholder,
  disabled,
  invalid,
  size: sz = "md",
  className,
  id,
  emptyText,
  renderValue,
  clearable,
  onClear,
  variant = "default",
  align,
  menuWidth,
  ...aria
}: SelectProps<V>) {
  const lb = useListbox(options, { searchable, disabled, align, menuWidth });
  const listId = useId();
  const selected = options.find((o) => o.value === value) ?? null;

  const openMenu = () => {
    if (disabled) return;
    lb.setOpen(true);
    const idx = lb.filtered.findIndex((o) => o.value === value);
    lb.setActive(idx >= 0 ? idx : 0);
  };

  const pick = (option: SelectOption<V>) => {
    onChange(option.value);
    lb.setOpen(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (!lb.open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      lb.move(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      lb.move(-1);
    } else if (e.key === "Home") {
      e.preventDefault();
      lb.setActive(0);
    } else if (e.key === "End") {
      e.preventDefault();
      lb.setActive(lb.filtered.length - 1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const option = lb.filtered[lb.active];
      if (option && !option.disabled) pick(option);
    } else if (e.key === "Escape" || e.key === "Tab") {
      lb.setOpen(false);
    } else if (e.key.length === 1) {
      lb.onTypeahead(e.key);
    }
  };

  return (
    <div className={cn("relative min-w-0", className)}>
      <button
        type="button"
        id={id}
        ref={lb.refs.setReference}
        {...lb.getReferenceProps({ onClick: () => (lb.open ? lb.setOpen(false) : openMenu()), onKeyDown })}
        disabled={disabled}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={lb.open}
        aria-controls={listId}
        aria-invalid={invalid || undefined}
        aria-label={aria["aria-label"]}
        className={cn(triggerClasses(sz, invalid, variant), "w-full", variant === "ghost" && "w-auto")}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {selected ? (
            renderValue ? (
              renderValue(selected)
            ) : (
              <>
                {selected.icon ? <span className="flex shrink-0 items-center [&>svg]:size-4">{selected.icon}</span> : null}
                <span className="truncate">{selected.label}</span>
              </>
            )
          ) : (
            <span className="truncate text-faint">{placeholder}</span>
          )}
        </span>
        {clearable && selected ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Effacer la sélection"
            onClick={(e) => {
              e.stopPropagation();
              onClear?.();
            }}
            className="rounded p-0.5 text-muted hover:text-ink"
          >
            <X className="size-3.5" />
          </span>
        ) : null}
        <ChevronDown className={cn("size-4 shrink-0 text-muted transition-transform", lb.open && "rotate-180")} aria-hidden />
      </button>
      <Menu lb={lb} searchable={searchable} searchPlaceholder={searchPlaceholder} emptyText={emptyText} listId={listId}>
        {lb.filtered.map((option, index) => (
          <OptionRow
            key={option.value}
            option={option}
            index={index}
            active={index === lb.active}
            selected={option.value === value}
            onPick={() => pick(option)}
            onHover={() => lb.setActive(index)}
          />
        ))}
      </Menu>
    </div>
  );
}

export interface MultiSelectProps<V extends string> extends BaseProps<V> {
  value: V[];
  onChange: (value: V[]) => void;
  maxChips?: number;
}

export function MultiSelect<V extends string>({
  value,
  onChange,
  options,
  placeholder = "Sélectionner",
  searchable = true,
  searchPlaceholder,
  disabled,
  invalid,
  size: sz = "md",
  className,
  id,
  emptyText,
  maxChips = 3,
  align,
  menuWidth,
  ...aria
}: MultiSelectProps<V>) {
  const lb = useListbox(options, { searchable, disabled, align, menuWidth });
  const listId = useId();
  const selected = options.filter((o) => value.includes(o.value));

  const toggle = (option: SelectOption<V>) => {
    if (value.includes(option.value)) onChange(value.filter((v) => v !== option.value));
    else onChange([...value, option.value]);
  };

  return (
    <div className={cn("relative min-w-0", className)}>
      <button
        type="button"
        id={id}
        ref={lb.refs.setReference}
        {...lb.getReferenceProps({
          onClick: () => {
            if (disabled) return;
            lb.setOpen(!lb.open);
            lb.setActive(0);
          },
          onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => {
            if (!lb.open && ["ArrowDown", "Enter", " "].includes(e.key)) {
              e.preventDefault();
              lb.setOpen(true);
              lb.setActive(0);
            } else if (lb.open && e.key === "Escape") lb.setOpen(false);
          },
        })}
        disabled={disabled}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={lb.open}
        aria-controls={listId}
        aria-label={aria["aria-label"]}
        className={cn(triggerClasses(sz, invalid), "h-auto min-h-10 w-full flex-wrap py-1.5")}
      >
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {selected.length === 0 ? <span className="text-faint">{placeholder}</span> : null}
          {selected.slice(0, maxChips).map((o) => (
            <span key={o.value} className="inline-flex h-6 items-center gap-1 rounded-md bg-panel-3 px-2 text-xs text-ink">
              {o.label}
              <span
                role="button"
                tabIndex={-1}
                aria-label={`Retirer ${o.label}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(o);
                }}
                className="text-muted hover:text-ink"
              >
                <X className="size-3" />
              </span>
            </span>
          ))}
          {selected.length > maxChips ? <span className="text-xs text-muted">+{selected.length - maxChips}</span> : null}
        </span>
        <ChevronDown className={cn("size-4 shrink-0 text-muted transition-transform", lb.open && "rotate-180")} aria-hidden />
      </button>
      <Menu lb={lb} searchable={searchable} searchPlaceholder={searchPlaceholder} emptyText={emptyText} listId={listId}>
        {lb.filtered.map((option, index) => (
          <OptionRow
            key={option.value}
            option={option}
            index={index}
            multiple
            active={index === lb.active}
            selected={value.includes(option.value)}
            onPick={() => toggle(option)}
            onHover={() => lb.setActive(index)}
          />
        ))}
      </Menu>
    </div>
  );
}

/** Free text input with custom suggestions (never a native datalist). */
export function Combobox({
  value,
  onChange,
  suggestions,
  placeholder,
  id,
  invalid,
  disabled,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  suggestions: string[];
  placeholder?: string;
  id?: string;
  invalid?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const listId = useId();
  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    transform: false,
    middleware: [
      offset(6),
      flip({ padding: 8 }),
      size({
        padding: 8,
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, { width: `${rects.reference.width}px`, maxHeight: `${Math.max(140, Math.min(280, availableHeight))}px` });
        },
      }),
    ],
  });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);
  const filtered = useMemo(() => {
    const q = normalize(value.trim());
    return suggestions.filter((s) => normalize(s).includes(q) && s !== value).slice(0, 12);
  }, [suggestions, value]);
  const pick = (s: string) => {
    onChange(s);
    setOpen(false);
  };
  return (
    <div className={cn("relative min-w-0", className)}>
      <input
        id={id}
        ref={refs.setReference}
        {...getReferenceProps()}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={open && filtered.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-invalid={invalid || undefined}
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setActive((a) => Math.min(filtered.length - 1, a + 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          } else if (e.key === "Enter" && open && active >= 0 && filtered[active]) {
            e.preventDefault();
            pick(filtered[active]!);
          } else if (e.key === "Escape") setOpen(false);
        }}
        className={cn(controlBase, "h-10 px-3", invalid && "border-danger/70")}
      />
      {open && filtered.length > 0 ? (
        <FloatingPortal>
          <div ref={refs.setFloating} style={placedStyles(floatingStyles, isPositioned)} {...getFloatingProps()} className={cn("z-[80] overflow-y-auto rounded-xl border border-line-strong bg-[#0d1730] p-1.5 shadow-pop", isPositioned && "animate-pop-in")}>
            <div id={listId} role="listbox">
              {filtered.map((s, i) => (
                <div
                  key={s}
                  role="option"
                  aria-selected={i === active}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(s)}
                  onMouseEnter={() => setActive(i)}
                  className={cn("cursor-pointer truncate rounded-lg px-2.5 py-2 text-[13px]", i === active && "bg-panel-3")}
                >
                  {s}
                </div>
              ))}
            </div>
          </div>
        </FloatingPortal>
      ) : null}
    </div>
  );
}
